/* The notification bell, made real — red alerts, pending approvals, budget
   warnings and recent deploys, derived from the live slices (no fake counts).
   Read state is per-device (localStorage) — web-push is the noted later step.
   Popover: labelled dialog, Esc closes, focus returns to the bell. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Badge } from '../../ds';
import { Kicker } from '../ui';
import { deriveNotifications, tone as toneColor, toneSoft, toneLine } from '../../lib/selectors';
import type { ApprovalItem, Budget, HealthTile, ActivityEvent } from '../../lib/contract';

const READ_KEY = 'mc.notifications.read';

function loadRead(): string[] {
  try {
    return (JSON.parse(localStorage.getItem(READ_KEY) ?? '[]') as string[]).slice(-100);
  } catch {
    return [];
  }
}

interface Props {
  approvals?: ApprovalItem[];
  health?: HealthTile[];
  budget?: Budget;
  activity?: ActivityEvent[];
}

export function NotificationsBell({ approvals, health, budget, activity }: Props) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<string[]>(loadRead);
  const btnRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const items = useMemo(
    () => deriveNotifications({ approvals, health, budget, activity }, readIds),
    [approvals, health, budget, activity, readIds],
  );
  const unread = items.filter((n) => !n.read).length;

  const markRead = (ids: string[]) => {
    const next = [...new Set([...readIds, ...ids])].slice(-100);
    setReadIds(next);
    try {
      localStorage.setItem(READ_KEY, JSON.stringify(next));
    } catch {
      /* private mode */
    }
  };

  // Esc closes; click-away closes; focus returns to the bell.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        (btnRef.current?.querySelector('button') as HTMLElement | null)?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!popRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <span ref={btnRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton variant="ghost" label={unread ? `Notifications — ${unread} unread` : 'Notifications'} active={open} onClick={() => setOpen((v) => !v)}>
        <span style={{ position: 'relative', display: 'flex' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          {unread > 0 && (
            <span aria-hidden="true" style={{ position: 'absolute', top: -4, right: -5, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 999, background: 'var(--danger)', color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
              {unread}
            </span>
          )}
        </span>
      </IconButton>

      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 'min(360px, calc(100vw - 24px))',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow)', zIndex: 'var(--z-overlay)' as unknown as number, overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', borderBottom: '1px solid var(--border)' }}>
            <Kicker>// notifications</Kicker>
            {items.length > 0 && (
              <button
                onClick={() => markRead(items.map((n) => n.id))}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontSize: 10.5, cursor: 'pointer', padding: 4 }}
              >
                mark all read
              </button>
            )}
          </div>
          <div className="mc-scroll" style={{ maxHeight: 340, overflow: 'auto' }}>
            {items.length === 0 && (
              <div style={{ padding: '22px 14px', color: 'var(--muted)', fontSize: 13, textAlign: 'center' }}>
                All clear — nothing needs your attention.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => {
                  markRead([n.id]);
                  setOpen(false);
                  nav(n.href);
                }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left',
                  padding: '11px 13px', minHeight: 44, background: 'none', border: 'none',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  opacity: n.read ? 0.55 : 1,
                }}
              >
                <span aria-hidden="true" style={{ width: 22, height: 22, flex: 'none', borderRadius: 7, display: 'grid', placeItems: 'center', background: toneSoft(n.tone), border: `1px solid ${toneLine(n.tone)}`, marginTop: 1 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: toneColor(n.tone) }} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: n.read ? 400 : 600, color: 'var(--ink)', lineHeight: 1.4 }}>{n.text}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{n.sub}</span>
                </span>
                {!n.read && <Badge tone="iris">new</Badge>}
              </button>
            ))}
          </div>
          <div style={{ padding: '9px 13px', borderTop: '1px solid var(--border)' }}>
            <Kicker>// web-push lands with per-user accounts</Kicker>
          </div>
        </div>
      )}
    </span>
  );
}
